import {memo, useCallback, useEffect, useState} from 'react';

import {view} from '@sail/ui';

import {
  attachmentsEnabled,
  inputEnabled,
  isMessageInteractivityEnabled,
  useConversationExceptions,
  useConversationInlineDataEvents,
} from '@stripe-internal/conversations-assistant';
import {ChatInput} from '@stripe-internal/conversations-ui';
import {Conversation} from '@stripe-internal/conversations-client';

import {MobileLog} from './MobileLog';

type Props = {
  conversation: Conversation;
};

export const MobileConversationScreen = memo(function ConversationScreen({
  conversation,
}: Props) {
  const [showInput, setShowInput] = useState<boolean>(
    inputEnabled(conversation),
  );
  const [disableAttachments, setDisableAttachments] = useState<boolean>(
    !attachmentsEnabled(conversation),
  );

  const [disableMessageInteractivity, setDisableMessageInteractivity] =
    useState<boolean>(!isMessageInteractivityEnabled(conversation));

  const bindToConversationState = useCallback(() => {
    setShowInput(inputEnabled(conversation));
    setDisableAttachments(!attachmentsEnabled(conversation));
    setDisableMessageInteractivity(
      !isMessageInteractivityEnabled(conversation),
    );
  }, [conversation]);

  useEffect(() => {
    bindToConversationState();
  }, [bindToConversationState]);

  useConversationExceptions(conversation);
  useConversationInlineDataEvents(conversation);

  return (
    <view.div
      data-testid="conversation-screen"
      css={{
        stack: 'y',
        height: 'fill',
      }}
      // Setting the `aria-live` attribute so that screen readers can read new messages coming in.
      // We set it to `polite` so that new message don't interrupt the reading of previous messages.
      aria-live="polite"
    >
      <MobileLog
        conversation={conversation}
        animated
        disableInteraction={disableMessageInteractivity}
      />
      <ChatInput
        conversation={conversation}
        attachmentsDisabled={disableAttachments}
        hidden={!showInput}
      />
    </view.div>
  );
});
